
import React, { useContext, useMemo } from "react";
import styles from "./Button.module.css";
import { ReactComponent as CheckIcon } from "../assets/images/icons/ic-check.svg";
import { ReactComponent as ErrorIcon } from "../assets/images/icons/ic-close.svg";
import { ButtonStatus } from "../common/constants";
import Spinner from "./Spinner";
import { useHaptic } from "react-haptic";
import { useLocation } from "react-router-dom";
import MainContext from "../common/MainContext";
import { canAccessPage, getPageKeyFromPath } from "../common/permissions";

const MUTATING_ACTION_LABELS_REGEX = /(aggiungi|modifica|elimina|rimuovi|salva|crea|duplica|sospendi|attiva|disattiva|cancella|pubblica|aggiorna|riordina|assegna|publish|delete|edit|add|remove|create|save|update|suspend|activate|deactivate|reorder|assign)/i;

const extractText = (node) => {
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }

    if (Array.isArray(node)) {
        return node.map(extractText).join(' ');
    }

    if (React.isValidElement(node)) {
        return extractText(node.props?.children);
    }

    return '';
};

const Button = React.forwardRef(({ id, children, inverse, appearance = 'default', onClick, disabled, style, additionalClass, accentColor, textColor, status, requiresEdit, permissionPage, ...props }, ref) => {
    const { vibrate } = useHaptic();
    const location = useLocation();
    const context = useContext(MainContext);

    const shouldHideForNoEditPermission = useMemo(() => {
        const user = context?.user;
        if (!user) {
            return false;
        }

        const pageKey = permissionPage ?? getPageKeyFromPath(location.pathname);
        const hasEditPermission = canAccessPage(user, pageKey, 'edit');
        if (hasEditPermission) {
            return false;
        }

        const mutatingByProp = typeof requiresEdit === 'boolean' ? requiresEdit : undefined;
        if (mutatingByProp !== undefined) {
            return mutatingByProp;
        }

        const buttonText = extractText(children).trim();
        return MUTATING_ACTION_LABELS_REGEX.test(buttonText);
    }, [context?.user, location.pathname, requiresEdit, permissionPage, children]);

    const handleOnClick = async (e) => {
        if (onClick && typeof onClick === 'function') {
            onClick(e);
        }
    };

    let classes = [];

    switch (appearance) {
        case 'default':
            classes.push(styles.actionButton);
            break;
        case 'text':
            classes.push(styles.textButton);
            break;
        case 'subtle':
            classes.push(styles.subtleButton);
            break;
        case 'social':
            classes.push(styles.socialButton);
            break;
        default:
            classes.push(styles.actionButton);
    }

    if (additionalClass) {
        classes.push(additionalClass);
    }

    if (inverse) {
        classes.push(styles.inverse);
    }

    if (shouldHideForNoEditPermission) {
        return null;
    }

    return (
        <button
            id={id}
            ref={ref}
            className={classes.join(' ')}
            style={{ ...style, '--button-accent-color': accentColor ? accentColor : 'var(--primary)', '--button-text-color': textColor ? textColor : 'white' }}
            onClick={(e) => {
                vibrate();
                handleOnClick(e);
            }}
            disabled={disabled}
            {...props}
        >
            {children}
            {status === ButtonStatus.Loading && <Spinner color={disabled ? 'rgba(var(--text-color-rgb), 20%)' : textColor}/>}
            {status === ButtonStatus.Success && <CheckIcon className={styles.successIcon} style={{color: disabled ? 'rgba(var(--text-color-rgb), 20%)' : textColor}} />}
            {status === ButtonStatus.Error && <ErrorIcon className={styles.errorIcon} style={{color: disabled ? 'rgba(var(--text-color-rgb), 20%)' : textColor}} />}
        </button>
    );
});

export default Button;
